import React, { useEffect, useRef, useState } from 'react'

import { Card, CardContent, Divider, Typography } from '@mui/material'

import RadioCardGroup from '../../../../shared/components/RadioCardGroup/RadioCardGroup.component'
import USPSLocationFinder from '../../../../shared/components/USPSLocationFinder/USPSLocationFinder.component'

import { getAirportZipCode } from '../../../../redux/states/flights/getAirportZipCode/getAirportZipCode.slice'
import { UspsLocation } from '../../../../redux/states/usps/getUspsLocations/getUspsLocations.types'
import { RootState } from '../../../../redux/store/store.types'
import { joinStringsWithBullet } from '../../../../shared/functions/String/string.functions'
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/redux.hooks'
import { ZipSource } from '../DeliveryDetails/DeliveryDetails.types'
import styles from './DropoffLocationStep.styles'
import { DropoffLocationStepProps, DropoffZipSource } from './DropoffLocationStep.types'

const ZIP_SOURCE_AIRPORT: ZipSource = 'airport'
const ZIP_SOURCE_CUSTOM: ZipSource = 'custom'

function formatStayAddressLine(deliveryDetails: DropoffLocationStepProps['deliveryDetails']): string {
  const { streetAddress, city, administrativeDivision } = deliveryDetails
  const parts = [streetAddress, city, administrativeDivision].filter(Boolean)

  return parts.join(', ')
}

const DropoffLocationStep: React.FC<DropoffLocationStepProps> = (props) => {
  const { deliveryDetails, onFormChange, arrivalAirportIataCode, radiusMiles, disabledLocationIDs } = props

  const [zipSource, setZipSource] = useState<DropoffZipSource>('airport')

  const dispatch = useAppDispatch()

  const { success: airportZipSuccess, error: airportZipError } = useAppSelector(
    (state: RootState) => state.getAirportZipCode
  )

  useEffect(() => {
    if (arrivalAirportIataCode && zipSource === 'airport') {
      dispatch(getAirportZipCode({ request: { iataCode: arrivalAirportIataCode } }))
    }
  }, [arrivalAirportIataCode, zipSource, dispatch])

  const airportZipCode = airportZipSuccess?.data?.zipCode ?? ''
  const stayPostalCode = deliveryDetails.postalCode ?? ''
  const initialZipCode = zipSource === 'airport' ? airportZipCode : stayPostalCode

  const handleZipSourceChange = (value: string) => {
    const nextSource = value as DropoffZipSource
    setZipSource(nextSource)
    const nextZipSource: ZipSource = nextSource === 'airport' ? ZIP_SOURCE_AIRPORT : ZIP_SOURCE_CUSTOM
    onFormChange(
      {
        ...deliveryDetails,
        zipSource: nextZipSource,
        ...(nextSource === 'stay' && { postalCode: stayPostalCode, customPostalCode: stayPostalCode })
      },
      Boolean(deliveryDetails.selectedUSPSLocation)
    )
  }

  const handleLocationSelect = (locationID: string, locationData: UspsLocation) => {
    const nextZipSource: ZipSource = zipSource === 'airport' ? ZIP_SOURCE_AIRPORT : ZIP_SOURCE_CUSTOM
    onFormChange(
      {
        ...deliveryDetails,
        zipSource: nextZipSource,
        selectedUSPSLocation: { locationID, locationData }
      },
      true
    )
  }

  // Keep radio card content stable during loading; only the locations area below reflects loading state
  const lastAirportDescriptionRef = useRef<string>('— • —')
  const airportDescription = airportZipError
    ? 'This airport is not in our list. Use the other option for your stay address.'
    : !arrivalAirportIataCode
      ? 'Select your arrival airport first'
      : airportZipSuccess?.data
        ? (() => {
            const description = joinStringsWithBullet([
              airportZipSuccess.data.airportName,
              airportZipSuccess.data.zipCode
            ])

            lastAirportDescriptionRef.current = description

            return description
          })()
        : lastAirportDescriptionRef.current

  const stayAddressLine = formatStayAddressLine(deliveryDetails)
  const stayDescription =
    stayPostalCode && stayAddressLine
      ? joinStringsWithBullet([stayAddressLine, stayPostalCode])
      : stayPostalCode
        ? joinStringsWithBullet(['—', stayPostalCode])
        : 'Enter your stay address in the previous step.'

  return (
    <Card sx={styles.card}>
      <CardContent>
        <Typography variant="h6" sx={styles.sectionTitle}>
          Where will you drop off your package?
        </Typography>

        <RadioCardGroup
          name="dropoffZipSource"
          value={zipSource}
          onChange={handleZipSourceChange}
          direction="row"
          aria-label="ZIP code source for USPS drop-off location"
          options={[
            {
              value: 'airport',
              title: 'Near the Airport',
              description: airportDescription,
              disabled: !arrivalAirportIataCode
            },
            {
              value: 'stay',
              title: 'Near My Stay Address',
              description: stayDescription
            }
          ]}
        />

        {initialZipCode && /^\d{5}$/.test(initialZipCode) && (
          <>
            <Divider sx={styles.divider} />

            <USPSLocationFinder
              recipientZipCode={initialZipCode}
              initialZipCode={initialZipCode}
              radiusMiles={radiusMiles}
              hideSearchForm
              onLocationSelect={handleLocationSelect}
              selectedLocationID={deliveryDetails.selectedUSPSLocation?.locationID}
              disabledLocationIDs={disabledLocationIDs}
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default DropoffLocationStep
